import { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Building,
  Calendar,
  CheckCircle,
  FileText,
  Layers,
  Loader2,
  MapPin,
  QrCode,
  User as UserIcon,
  X,
} from 'lucide-react';
import { api, extractErrorMessage } from '../../lib/api';
import { DocumentsList } from './DocumentsList';
import { HistoriqueTimeline } from './HistoriqueTimeline';
import { ModalPortal } from '../ModalPortal';
import { PdfExportButton } from '../shared/PdfExportButton';
import { QrLabelPrint } from '../shared/QrLabelPrint';

/**
 * Drawer détail salle (US-BAT-04 + 06 + 07).
 */
interface SalleDetail {
  id: string;
  code: string;
  numero_porte: string | null;
  nom: string;
  type: string;
  statut: string;
  surface: number | null;
  est_reservable: boolean;
  duree_max_reservation: number | null;
  preavis_requis: number | null;
  validation_requise: boolean;
  custom_fields: Record<string, unknown>;
  etage: {
    id: string;
    numero: string;
    nom: string | null;
    batiment: { id: string; code: string; nom: string };
  };
  service: { id: string; nom_complet: string } | null;
  responsable: { id: string; first_name: string; last_name: string } | null;
}

export function SalleDetailDrawer({ salleId, onClose }: { salleId: string; onClose(): void }) {
  const [detail, setDetail] = useState<SalleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tab, setTab] = useState<'info' | 'documents' | 'qr' | 'historique'>('info');
  const [affectModalOpen, setAffectModalOpen] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  async function reload() {
    const res = await api.get<{ data: SalleDetail }>(`/salles/${salleId}`);
    setDetail(res.data.data);
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: SalleDetail }>(`/salles/${salleId}`);
        setDetail(res.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [salleId]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Layers className="w-5 h-5 mr-2 text-primary-600" />
            Détail salle
          </h2>
          <div className="flex items-center space-x-1">
            {detail && (
              <PdfExportButton
                targetRef={pdfRef}
                filename={`fiche-salle-${detail.code}.pdf`}
                label="PDF"
                onBeforeExport={() => setTab('info')}
              />
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 flex-shrink-0">
          <nav className="flex">
            <TabButton active={tab === 'info'} onClick={() => setTab('info')}>
              Informations
            </TabButton>
            <TabButton active={tab === 'documents'} onClick={() => setTab('documents')}>
              <FileText className="w-3.5 h-3.5 inline mr-1" />
              Documents
            </TabButton>
            <TabButton active={tab === 'qr'} onClick={() => setTab('qr')}>
              <QrCode className="w-3.5 h-3.5 inline mr-1" />
              QR
            </TabButton>
            <TabButton active={tab === 'historique'} onClick={() => setTab('historique')}>
              Historique
            </TabButton>
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}

          {loading || !detail ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : tab === 'info' ? (
            <div ref={pdfRef} className="space-y-4">
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Identité
                </h3>
                <dl className="text-sm space-y-1.5">
                  <Row label="Code" value={detail.code} />
                  <Row label="N° de porte" value={detail.numero_porte ?? '—'} />
                  <Row label="Nom" value={detail.nom} />
                  <Row label="Type" value={detail.type} />
                  <Row label="Statut" value={detail.statut.replace(/_/g, ' ')} />
                  <Row label="Surface" value={detail.surface ? `${detail.surface} m²` : '—'} />
                </dl>
              </section>

              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                  <MapPin className="w-3.5 h-3.5 mr-1" />
                  Localisation
                </h3>
                <p className="text-sm text-gray-700">
                  Bâtiment{' '}
                  <span className="font-medium">
                    {detail.etage.batiment.code} — {detail.etage.batiment.nom}
                  </span>
                  <br />
                  Étage <span className="font-medium">{detail.etage.numero}</span>
                  {detail.etage.nom && ` (${detail.etage.nom})`}
                </p>
              </section>

              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center">
                    <Building className="w-3.5 h-3.5 mr-1" />
                    Affectation
                  </h3>
                  <button
                    type="button"
                    onClick={() => setAffectModalOpen(true)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    {detail.service ? 'Modifier' : 'Affecter à un service'}
                  </button>
                </div>
                {detail.service ? (
                  <p className="text-sm text-gray-700">Service : {detail.service.nom_complet}</p>
                ) : (
                  <p className="text-sm text-gray-500 italic">Aucun service affecté.</p>
                )}
                {detail.responsable && (
                  <p className="text-sm text-gray-700 inline-flex items-center mt-1">
                    <UserIcon className="w-3.5 h-3.5 mr-1 text-gray-400" />
                    Responsable : {detail.responsable.first_name} {detail.responsable.last_name}
                  </p>
                )}
              </section>

              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center">
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  Réservation
                </h3>
                {detail.est_reservable ? (
                  <div className="text-sm space-y-1">
                    <p className="text-green-600 inline-flex items-center">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />
                      Salle réservable
                    </p>
                    {detail.duree_max_reservation && (
                      <p className="text-gray-600">
                        Durée max : {detail.duree_max_reservation} min
                      </p>
                    )}
                    {detail.preavis_requis && (
                      <p className="text-gray-600">Préavis requis : {detail.preavis_requis} h</p>
                    )}
                    {detail.validation_requise && (
                      <p className="text-amber-600">⚠️ Validation par un responsable requise</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">Salle non réservable.</p>
                )}
              </section>

              {Object.keys(detail.custom_fields ?? {}).length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Champs personnalisés
                  </h3>
                  <dl className="text-sm space-y-1.5">
                    {Object.entries(detail.custom_fields).map(([k, v]) => (
                      <Row key={k} label={k} value={String(v ?? '—')} />
                    ))}
                  </dl>
                </section>
              )}
            </div>
          ) : tab === 'documents' ? (
            <DocumentsList entityType="salle" entityId={salleId} />
          ) : tab === 'qr' ? (
            <QrLabelPrint
              code={detail.code}
              label={detail.nom}
              url={`${window.location.origin}/salles/${detail.id}`}
              sublabel={detail.numero_porte ? `Porte ${detail.numero_porte}` : undefined}
            />
          ) : (
            <HistoriqueTimeline entityType="salle" entityId={salleId} />
          )}
        </div>
      </aside>
      {affectModalOpen && detail && (
        <AffectServiceModal
          salleId={salleId}
          currentServiceId={detail.service?.id ?? null}
          onClose={() => setAffectModalOpen(false)}
          onSaved={async () => {
            setAffectModalOpen(false);
            try {
              await reload();
            } catch (err) {
              setErrorMessage(extractErrorMessage(err));
            }
          }}
        />
      )}
    </ModalPortal>
  );
}

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

function AffectServiceModal({
  salleId,
  currentServiceId,
  onClose,
  onSaved,
}: {
  salleId: string;
  currentServiceId: string | null;
  onClose(): void;
  onSaved(): void;
}) {
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [serviceId, setServiceId] = useState<string>(currentServiceId ?? '');
  const [typeUtilisation, setTypeUtilisation] = useState<'principal' | 'secondaire' | 'partage'>(
    'principal',
  );
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: ServiceOption[] }>('/services', {
          params: { statut: 'actif', sort_by: 'nom_complet', page_size: 200 },
        });
        setServices(res.data.data);
      } catch (err) {
        setErrMsg(extractErrorMessage(err));
      }
    })();
  }, []);

  async function submit() {
    setSaving(true);
    setErrMsg(null);
    try {
      await api.post(`/salles/${salleId}/affect`, {
        service_id: serviceId || null,
        type_utilisation: typeUtilisation,
      });
      onSaved();
    } catch (err) {
      setErrMsg(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-base font-semibold text-gray-900">Affecter à un service</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {errMsg && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errMsg}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">— Détacher (aucun) —</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.nom_complet}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type d'utilisation
            </label>
            <select
              value={typeUtilisation}
              onChange={(e) => setTypeUtilisation(e.target.value as typeof typeUtilisation)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={!serviceId}
            >
              <option value="principal">Principal</option>
              <option value="secondaire">Secondaire</option>
              <option value="partage">Partagé</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick(): void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'text-primary-600 border-primary-500 bg-primary-50/50'
          : 'text-gray-600 border-transparent hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-gray-500 text-xs">{label}</dt>
      <dd className="text-gray-900 text-right break-all">{value}</dd>
    </div>
  );
}
